// Store-level checkout settings ("Veikala iestatījumi"): per-unit gift-wrap
// price. Backed by /admin/store-settings, persisted in the store_settings
// key/value table. ("Saņemt atdzesētu" is per-customer — see CustomerDetail.)

import { useEffect, useState } from "react";
import { Gift, Save, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

const sectionClass = "bg-card border border-border rounded-lg p-5 space-y-4";
const inputClass =
  "w-full bg-card border border-border rounded-lg px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground";
const labelClass = "font-body text-xs font-medium text-muted-foreground mb-1 block";

type Settings = {
  giftWrapPriceCents: number;
};

export function StoreSettings() {
  const { toast } = useToast();
  const t = useT();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [giftWrapPrice, setGiftWrapPrice] = useState("3.00");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api<Settings>("/api/v1/admin/store-settings");
        if (cancelled) return;
        setGiftWrapPrice((s.giftWrapPriceCents / 100).toFixed(2));
      } catch {
        if (!cancelled) {
          toast({
            title: t("storeSettings.errorLoading", "Neizdevās ielādēt iestatījumus"),
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, toast]);

  const save = async () => {
    const priceNum = Number(giftWrapPrice.replace(",", "."));
    if (!Number.isFinite(priceNum) || priceNum < 0) {
      toast({
        title: t("storeSettings.invalidPrice", "Nederīga cena"),
        variant: "destructive",
      });
      return;
    }
    setSaving(true);
    try {
      const s = await api<Settings>("/api/v1/admin/store-settings", {
        method: "PUT",
        body: JSON.stringify({
          giftWrapPriceCents: Math.round(priceNum * 100),
        }),
      });
      setGiftWrapPrice((s.giftWrapPriceCents / 100).toFixed(2));
      toast({ title: t("storeSettings.saved", "Iestatījumi saglabāti") });
    } catch {
      toast({
        title: t("storeSettings.errorSaving", "Neizdevās saglabāt"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
          <Store size={20} className="text-muted-foreground" />
          {t("storeSettings.title", "Veikala iestatījumi")}
        </h1>
        <Button onClick={save} disabled={loading || saving}>
          <Save size={14} />
          {saving ? t("common.saving", "Saglabā…") : t("common.save", "Saglabāt")}
        </Button>
      </div>

      <section className={sectionClass}>
        <h2 className="flex items-center gap-2 font-display text-sm font-bold text-foreground">
          <Gift size={16} className="text-muted-foreground" />
          {t("storeSettings.giftWrapTitle", "Dāvanu iesaiņojums")}
        </h2>
        <p className="font-body text-xs text-muted-foreground">
          {t(
            "storeSettings.giftWrapHint",
            "Cena par vienu iesaiņotu preci. Pircējs kasē izvēlas, kuras preces (un cik) iesaiņot.",
          )}
        </p>
        <div className="max-w-[200px]">
          <label className={labelClass}>
            {t("storeSettings.giftWrapPrice", "Cena par vienību (€)")}
          </label>
          <input
            className={inputClass}
            inputMode="decimal"
            value={giftWrapPrice}
            onChange={(e) => setGiftWrapPrice(e.target.value)}
            disabled={loading}
          />
        </div>
      </section>

    </div>
  );
}
